import { TablePagination } from '@/components/core/async-data-table/table-pagination';
import { EditVariantDialog } from '@/components/products/form/section/variant/edit-variant-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useVariantEdit } from '@/hooks/product/use-variant-edit';
import { ProductVariant, ProductWithVariantsStock, VariantStockSearchItem } from '@/types/product-type';
import { ChevronsDownUp, ChevronsUpDown, DollarSign, Loader2, Package, Search, TrendingUp, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { ProductTreeRow } from './product-tree-row';

interface ProductTreeTableProps {
  variants: VariantStockSearchItem[];
  isLoading?: boolean;
  pagination?: {
    totalElements: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    isEmpty: boolean;
  };
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  onRefresh?: () => void;
}

function groupVariantsByProduct(items: VariantStockSearchItem[]): ProductWithVariantsStock[] {
  const productMap = new Map<string, ProductWithVariantsStock>();

  items.forEach((item) => {
    if (!productMap.has(item.productId)) {
      productMap.set(item.productId, {
        productId: item.productId,
        productName: item.productName,
        productImageUrl: item.productImageUrl,
        productPrice: item.productPrice,
        isActive: item.isActive,
        variants: [],
      });
    }

    const product = productMap.get(item.productId)!;
    product.variants.push(item);
  });

  return Array.from(productMap.values());
}

export function ProductTreeTable({
  variants,
  isLoading = false,
  pagination,
  page,
  setPage,
  pageSize,
  setPageSize,
  onRefresh,
}: ProductTreeTableProps) {
  const groupedProducts = useMemo(() => groupVariantsByProduct(variants), [variants]);

  // Advanced code search — client-side filter on already-fetched data
  const [advancedCode, setAdvancedCode] = useState('');
  // Global collapse/expand state — null means each row manages itself
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const displayedProducts = useMemo(() => {
    const trimmed = advancedCode.trim().toLowerCase();
    if (!trimmed) return groupedProducts;
    return groupedProducts.filter((p) => {
      // Match against product name or any variant SKU / saleCode
      const nameMatch = (p.productName ?? '').toLowerCase().includes(trimmed);
      const skuMatch = p.variants.some(
        (v) =>
          (v.sku ?? '').toLowerCase().includes(trimmed) ||
          (v.saleCode ?? '').toLowerCase().includes(trimmed)
      );
      return nameMatch || skuMatch;
    });
  }, [groupedProducts, advancedCode]);

  // Use the variant edit hook
  const firstVariant = variants[0];
  const productId = firstVariant?.productId?.toString() || '';

  const {
    isEditDialogOpen,
    setIsEditDialogOpen,
    editingVariant,
    isUpdating,
    handleEdit,
    handleEditSubmit,
    handleCancel,
  } = useVariantEdit({
    productId,
    onSuccess: onRefresh,
  });


  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading products...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!pagination || pagination.isEmpty) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No products found</p>
        </CardContent>
      </Card>
    );
  }

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  function handleEditVariant(variant: ProductVariant): void {
    handleEdit(variant);
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* ── Toolbar ── */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-3 py-2.5 border-b border-slate-100 bg-slate-50/60">
          {/* Advanced code / SKU search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={advancedCode}
              onChange={(e) => setAdvancedCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setAdvancedCode('')}
              placeholder="Filter by product name / SKU / sale code…"
              className="w-full pl-8 pr-8 h-9 rounded-md border border-slate-200 bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder:text-slate-400"
            />
            {advancedCode && (
              <button
                type="button"
                onClick={() => setAdvancedCode('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Collapse / Expand all toggle */}
          <button
            type="button"
            onClick={() => setIsCollapsed((prev) => !prev)}
            className="inline-flex items-center gap-1.5 shrink-0 h-9 px-3 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
            aria-label={isCollapsed ? 'Expand all rows' : 'Collapse all rows'}
          >
            {isCollapsed ? (
              <><ChevronsDownUp className="h-3.5 w-3.5" /> Expand all</>
            ) : (
              <><ChevronsUpDown className="h-3.5 w-3.5" /> Collapse all</>
            )}
          </button>
        </div>
        <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm w-full max-w-full overflow-x-auto">
          <div className="min-w-full">
            <Table className="w-full">
              <TooltipProvider>
                <TableHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold text-slate-700 py-2 px-2 text-xs sm:text-sm">
                      Product
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 py-2 px-1.5 sm:px-2 text-center text-xs sm:text-sm">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-center gap-1 sm:gap-1.5 cursor-help">
                            <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                            <span className="text-xs sm:text-sm whitespace-nowrap">Stock</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs space-y-1">
                            <div>
                              Shows inflows (received, adjustments in) and outflows (sold,
                              adjustments out)
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TableHead>
                    <TableHead className="hidden sm:table-cell font-semibold text-slate-700 py-2 px-2 text-right text-xs sm:text-sm">
                      <div className="flex items-center justify-end gap-1 sm:gap-1.5">
                        <DollarSign className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                        <span className="text-xs sm:text-sm whitespace-nowrap">Price</span>
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 py-2 px-2 text-center text-xs sm:text-sm">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
              </TooltipProvider>
              <TableBody>
                {displayedProducts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-10 text-sm text-slate-400">
                      {advancedCode ? `No products matching "${advancedCode}"` : 'No products found'}
                    </td>
                  </tr>
                ) : (
                  displayedProducts.map((product, index) => (
                    <ProductTreeRow
                      key={product.productId}
                      product={product}
                      isLast={index === displayedProducts.length - 1}
                      onEditVariant={handleEditVariant}
                      isCollapsed={isCollapsed}
                    />
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              count={pagination.totalElements}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
            {/* Edit Variant Dialog */}
            <EditVariantDialog
              open={isEditDialogOpen}
              onOpenChange={setIsEditDialogOpen}
              editingVariant={editingVariant}
              productId={productId}
              productCode={''}
              onSubmit={handleEditSubmit}
              onCancel={handleCancel}
              isLoading={isUpdating}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
